/*
 * LICENSE PROTECTED SOFTWARE
 *
 * Bu yazilim lisans korumasi altindadir.
 * Gecerli lisans olmadan kullanilmasi yasaktir.
 *
 * Kaynak kodunun izinsiz erisimi, kopyalanmasi veya dagitimi
 * hukuki yaptirimslara tabidir.
 * (c) 2026  @cemdemirci0101
 *  bilgi@cemdemirci.com
 * This source code is proprietary and confidential.
 * Unauthorized copying, modification, or distribution
 * of this file, via any medium, is strictly prohibited.
 * (c) 2026 All Rights Reserved.
 */

import React, { useState, useEffect, useRef } from 'react';
import { useAuth } from '../context/AuthContext';
import { usePageTitle } from '../hooks/usePageTitle';
import api, { usersAPI, locationsAPI } from '../lib/api';
import { formatDateTime, USER_ROLES, formatApiErrorDetail } from '../lib/utils';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { toast } from 'sonner';
import {
  User, Mail, Phone, MapPin, Calendar, Shield, Edit, Save, Loader2, Lock,
  Camera, Trash2, Bell, AlertTriangle, Eye, EyeOff, CreditCard, Upload, X, ShieldCheck, GraduationCap, Briefcase,
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CINSIYET_MAP = { erkek: 'Erkek', kadin: 'Kadin', belirtmek_istemiyor: 'Belirtmek Istemiyor' };
const EGITIM_OPTIONS = [
  { value: 'ilkokul', label: 'Ilkokul' },
  { value: 'ortaokul', label: 'Ortaokul' },
  { value: 'lise', label: 'Lise' },
  { value: 'onlisans', label: 'Onlisans' },
  { value: 'lisans', label: 'Lisans' },
  { value: 'yuksek_lisans', label: 'Yuksek Lisans' },
];
const EGITIM_MAP = Object.fromEntries(EGITIM_OPTIONS.map(e => [e.value, e.label]));

// Avatar Component
const AvatarSection = ({ user, onAvatarChange }) => {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const handleUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const ext = file.name.split('.').pop().toLowerCase();
    if (!['jpg', 'jpeg', 'png', 'webp'].includes(ext)) {
      toast.error('Sadece jpg, png, webp yuklenebilir');
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      toast.error('Dosya boyutu 5MB\'yi asamaz');
      return;
    }
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const r = await api.post('/auth/avatar', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Profil resmi guncellendi');
      onAvatarChange(r.data.avatar_url);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || 'Yuklenemedi');
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleRemove = async () => {
    try {
      await api.delete('/auth/avatar');
      toast.success('Profil resmi silindi');
      onAvatarChange(null);
    } catch { toast.error('Silinemedi'); }
  };

  const avatarSrc = user?.avatar_url ? `${API_URL}${user.avatar_url}?t=${Date.now()}` : null;
  const initials = `${user?.name?.charAt(0) || ''}${user?.surname?.charAt(0) || ''}`;

  return (
    <div className="flex items-center gap-5" data-testid="avatar-section">
      <div className="relative group">
        <div className="w-24 h-24 rounded-2xl overflow-hidden border-2 border-slate-200 shadow-sm">
          {avatarSrc ? (
            <img src={avatarSrc} alt="Avatar" className="w-full h-full object-cover" />
          ) : (
            <div className="w-full h-full bg-gradient-to-br from-blue-500 to-indigo-600 flex items-center justify-center">
              <span className="text-3xl font-bold text-white">{initials}</span>
            </div>
          )}
        </div>
        <button
          onClick={() => fileRef.current?.click()}
          className="absolute inset-0 rounded-2xl bg-black/40 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center"
          disabled={uploading}
          data-testid="avatar-upload-trigger"
        >
          {uploading ? <Loader2 className="w-6 h-6 text-white animate-spin" /> : <Camera className="w-6 h-6 text-white" />}
        </button>
        <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" onChange={handleUpload} className="hidden" data-testid="avatar-file-input" />
      </div>
      <div className="space-y-1.5">
        <h2 className="text-xl font-bold text-slate-900">{user?.name} {user?.surname}</h2>
        <p className="text-sm text-slate-500">{user?.email}</p>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => fileRef.current?.click()} disabled={uploading} data-testid="avatar-change-btn">
            <Upload className="w-3.5 h-3.5 mr-1" /> {avatarSrc ? 'Degistir' : 'Foto Yukle'}
          </Button>
          {avatarSrc && (
            <Button variant="ghost" size="sm" onClick={handleRemove} className="text-red-500 hover:text-red-700" data-testid="avatar-remove-btn">
              <Trash2 className="w-3.5 h-3.5 mr-1" /> Kaldir
            </Button>
          )}
        </div>
      </div>
    </div>
  );
};

// Profile Info Tab
const ProfileInfoTab = ({ user, checkAuth }) => {
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({});
  const [iller, setIller] = useState([]);
  const [ilceler, setIlceler] = useState([]);
  const [mahalleler, setMahalleler] = useState([]);

  useEffect(() => {
    if (user) {
      setForm({
        name: user.name || '', surname: user.surname || '', phone: user.phone || '',
        dogum_tarihi: user.dogum_tarihi || '', acik_adres: user.acik_adres || '',
        cinsiyet: user.cinsiyet || '', egitim_durumu: user.egitim_durumu || '',
        meslek: user.meslek || '',
        il_id: user.il_id || '', ilce_id: user.ilce_id || '', mahalle_id: user.mahalle_id || '',
      });
    }
  }, [user]);

  useEffect(() => {
    locationsAPI.getIller().then(r => setIller(r.data)).catch(() => {});
  }, []);

  useEffect(() => {
    if (form.il_id) locationsAPI.getIlceler(form.il_id).then(r => setIlceler(r.data)).catch(() => {});
  }, [form.il_id]);

  useEffect(() => {
    if (form.ilce_id) locationsAPI.getMahalleler(form.ilce_id).then(r => setMahalleler(r.data)).catch(() => {});
  }, [form.ilce_id]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await usersAPI.update(user.id, form);
      toast.success('Profiliniz guncellendi');
      setEditing(false);
      checkAuth();
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail) || 'Guncelleme basarisiz'); }
    finally { setSaving(false); }
  };

  const InfoItem = ({ icon: Icon, label, value }) => (
    <div className="flex items-center gap-3 py-3">
      <div className="w-9 h-9 rounded-lg bg-slate-100 flex items-center justify-center shrink-0"><Icon className="w-4 h-4 text-slate-500" /></div>
      <div className="min-w-0 flex-1"><p className="text-xs text-slate-400">{label}</p><p className="text-sm font-medium text-slate-800 truncate">{value || '-'}</p></div>
    </div>
  );

  const ilName = iller.find(i => i.id === user?.il_id)?.name;
  const ilceName = ilceler.find(i => i.id === user?.ilce_id)?.name;
  const mahalleName = mahalleler.find(m => m.id === user?.mahalle_id)?.name;

  if (editing) {
    return (
      <div className="space-y-4" data-testid="profile-edit-form">
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Ad</Label><Input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} data-testid="profile-name" /></div>
          <div><Label>Soyad</Label><Input value={form.surname} onChange={e => setForm(f => ({ ...f, surname: e.target.value }))} data-testid="profile-surname" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Telefon</Label><Input value={form.phone} onChange={e => setForm(f => ({ ...f, phone: e.target.value }))} placeholder="05XX XXX XX XX" data-testid="profile-phone" /></div>
          <div><Label>Dogum Tarihi</Label><Input type="date" value={form.dogum_tarihi} onChange={e => setForm(f => ({ ...f, dogum_tarihi: e.target.value }))} data-testid="profile-dogum" /></div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Cinsiyet</Label>
            <Select value={form.cinsiyet || 'none'} onValueChange={v => setForm(f => ({ ...f, cinsiyet: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="profile-cinsiyet"><SelectValue placeholder="Seciniz" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Secilmedi</SelectItem>
                {Object.entries(CINSIYET_MAP).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Egitim Durumu</Label>
            <Select value={form.egitim_durumu || 'none'} onValueChange={v => setForm(f => ({ ...f, egitim_durumu: v === 'none' ? '' : v }))}>
              <SelectTrigger data-testid="profile-egitim"><SelectValue placeholder="Seciniz" /></SelectTrigger>
              <SelectContent>
                <SelectItem value="none">Secilmedi</SelectItem>
                {EGITIM_OPTIONS.map(e => <SelectItem key={e.value} value={e.value}>{e.label}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div><Label>Mesleğiniz</Label>
            <Input
              value={form.meslek || ''}
              onChange={e => setForm(f => ({ ...f, meslek: e.target.value }))}
              placeholder="Örn: Öğretmen, Mühendis, Öğrenci..."
              maxLength={100}
              data-testid="profile-meslek"
            />
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          <div><Label>Il</Label>
            <Select value={form.il_id || 'none'} onValueChange={v => { setForm(f => ({ ...f, il_id: v === 'none' ? '' : v, ilce_id: '', mahalle_id: '' })); setMahalleler([]); }}>
              <SelectTrigger><SelectValue placeholder="Il" /></SelectTrigger>
              <SelectContent>{iller.map(il => <SelectItem key={il.id} value={il.id}>{il.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Ilce</Label>
            <Select value={form.ilce_id || 'none'} onValueChange={v => setForm(f => ({ ...f, ilce_id: v === 'none' ? '' : v, mahalle_id: '' }))}>
              <SelectTrigger><SelectValue placeholder="Ilce" /></SelectTrigger>
              <SelectContent>{ilceler.map(i => <SelectItem key={i.id} value={i.id}>{i.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          <div><Label>Mahalle</Label>
            <Select value={form.mahalle_id || 'none'} onValueChange={v => setForm(f => ({ ...f, mahalle_id: v === 'none' ? '' : v }))}>
              <SelectTrigger><SelectValue placeholder="Mahalle" /></SelectTrigger>
              <SelectContent>{mahalleler.map(m => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}</SelectContent>
            </Select>
          </div>
        </div>
        <div><Label>Acik Adres</Label><Textarea rows={2} value={form.acik_adres} onChange={e => setForm(f => ({ ...f, acik_adres: e.target.value }))} placeholder="Sokak, bina no..." data-testid="profile-adres" /></div>
        <div className="flex justify-end gap-3 pt-3 border-t">
          <Button variant="outline" onClick={() => setEditing(false)}>Iptal</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="profile-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Kaydet</Button>
        </div>
      </div>
    );
  }

  return (
    <div data-testid="profile-info">
      <div className="flex justify-end mb-2">
        <Button variant="outline" size="sm" onClick={() => setEditing(true)} data-testid="profile-edit-btn"><Edit className="w-4 h-4 mr-1" /> Duzenle</Button>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x divide-slate-100">
        <div className="space-y-0 pr-0 sm:pr-4">
          <InfoItem icon={Mail} label="E-posta" value={user?.email} />
          <InfoItem icon={Phone} label="Telefon" value={user?.phone} />
          <InfoItem icon={CreditCard} label="TC Kimlik No" value={user?.tc_kimlik} />
          <InfoItem icon={Calendar} label="Dogum Tarihi" value={user?.dogum_tarihi ? new Date(user.dogum_tarihi).toLocaleDateString('tr-TR') : null} />
          <InfoItem icon={User} label="Cinsiyet" value={CINSIYET_MAP[user?.cinsiyet]} />
        </div>
        <div className="space-y-0 pl-0 sm:pl-4">
          <InfoItem icon={GraduationCap} label="Egitim Durumu" value={EGITIM_MAP[user?.egitim_durumu]} />
          <InfoItem icon={Briefcase} label="Meslek" value={user?.meslek} />
          <InfoItem icon={MapPin} label="Konum" value={[ilName, ilceName, mahalleName].filter(Boolean).join(', ') || null} />
          <InfoItem icon={MapPin} label="Acik Adres" value={user?.acik_adres} />
          <InfoItem icon={Shield} label="Rol" value={USER_ROLES[user?.role]?.label || user?.role} />
          <InfoItem icon={Calendar} label="Kayit Tarihi" value={formatDateTime(user?.created_at)} />
        </div>
      </div>
    </div>
  );
};

// Security Tab
const SecurityTab = ({ user }) => {
  const [showPwForm, setShowPwForm] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current: '', new_password: '', confirm: '' });
  const [saving, setSaving] = useState(false);

  const handleChangePassword = async () => {
    if (pw.new_password.length < 8) { toast.error('Yeni sifre en az 8 karakter'); return; }
    if (!/[A-Za-z]/.test(pw.new_password)) { toast.error('Sifre en az bir harf icermeli'); return; }
    if (!/[0-9]/.test(pw.new_password)) { toast.error('Sifre en az bir rakam icermeli'); return; }
    if (!/[^A-Za-z0-9]/.test(pw.new_password)) { toast.error('Sifre en az bir ozel karakter icermeli'); return; }
    if (pw.new_password !== pw.confirm) { toast.error('Sifreler eslesmedi'); return; }
    setSaving(true);
    try {
      await api.post('/auth/change-password', { current_password: pw.current, new_password: pw.new_password });
      toast.success('Sifreniz degistirildi');
      setShowPwForm(false);
      setPw({ current: '', new_password: '', confirm: '' });
    } catch (e) { toast.error(e.response?.data?.detail || 'Sifre degistirilemedi'); }
    finally { setSaving(false); }
  };

  return (
    <div className="space-y-6" data-testid="security-tab">
      <Card className="shadow-sm">
        <CardContent className="p-5">
          {showPwForm ? (
            <div className="space-y-4 max-w-md">
              <h3 className="font-semibold text-slate-800 flex items-center gap-2"><Lock className="w-4 h-4" /> Sifre Degistir</h3>
              <div>
                <Label>Mevcut Sifre</Label>
                <div className="relative">
                  <Input type={showPw ? 'text' : 'password'} value={pw.current} onChange={e => setPw(p => ({ ...p, current: e.target.value }))} data-testid="pw-current" />
                  <button type="button" onClick={() => setShowPw(!showPw)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">{showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}</button>
                </div>
              </div>
              <div><Label>Yeni Sifre</Label><Input type={showPw ? 'text' : 'password'} value={pw.new_password} onChange={e => setPw(p => ({ ...p, new_password: e.target.value }))} data-testid="pw-new" /></div>
              <div><Label>Yeni Sifre (Tekrar)</Label><Input type={showPw ? 'text' : 'password'} value={pw.confirm} onChange={e => setPw(p => ({ ...p, confirm: e.target.value }))} data-testid="pw-confirm" /></div>
              {pw.new_password && pw.new_password.length < 8 && <p className="text-xs text-red-500">En az 8 karakter</p>}
              {pw.new_password && pw.new_password.length >= 8 && !/[A-Za-z]/.test(pw.new_password) && <p className="text-xs text-red-500">En az bir harf icermeli</p>}
              {pw.new_password && pw.new_password.length >= 8 && /[A-Za-z]/.test(pw.new_password) && !/[0-9]/.test(pw.new_password) && <p className="text-xs text-red-500">En az bir rakam icermeli</p>}
              {pw.new_password && pw.new_password.length >= 8 && /[A-Za-z]/.test(pw.new_password) && /[0-9]/.test(pw.new_password) && !/[^A-Za-z0-9]/.test(pw.new_password) && <p className="text-xs text-red-500">En az bir ozel karakter icermeli</p>}
              {pw.confirm && pw.new_password !== pw.confirm && <p className="text-xs text-red-500">Sifreler eslesmedi</p>}
              <div className="flex gap-3 pt-2">
                <Button variant="outline" onClick={() => setShowPwForm(false)}>Iptal</Button>
                <Button onClick={handleChangePassword} disabled={saving} data-testid="pw-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Lock className="w-4 h-4 mr-1" />} Degistir</Button>
              </div>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-slate-100 flex items-center justify-center"><Lock className="w-5 h-5 text-slate-500" /></div>
                <div><p className="font-medium text-slate-800">Sifre</p><p className="text-xs text-slate-400">Hesabinizin guvenligini saglayin</p></div>
              </div>
              <Button variant="outline" size="sm" onClick={() => setShowPwForm(true)} data-testid="pw-change-btn"><Lock className="w-3.5 h-3.5 mr-1" /> Sifre Degistir</Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

// Notifications Tab
const NotificationsTab = ({ user, checkAuth }) => {
  const [prefs, setPrefs] = useState({ email: true, push: true, sms: false, voice: false });
  const [izni, setIzni] = useState({ email: false, sms: false, voice: false });
  const [saving, setSaving] = useState(false);
  const [savingIzni, setSavingIzni] = useState(false);

  useEffect(() => {
    if (user?.notification_prefs) setPrefs(user.notification_prefs);
    if (user?.iletisim_izni) setIzni(user.iletisim_izni);
  }, [user]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.put('/auth/notification-prefs', prefs);
      toast.success('Bildirim tercihleri güncellendi');
      checkAuth();
    } catch { toast.error('Güncellenemedi'); }
    finally { setSaving(false); }
  };

  const handleSaveIzni = async () => {
    setSavingIzni(true);
    try {
      await api.put('/auth/iletisim-izni', izni);
      toast.success('İletişim izni tercihleri güncellendi');
      checkAuth();
    } catch { toast.error('Güncellenemedi'); }
    finally { setSavingIzni(false); }
  };

  const NotifRow = ({ label, desc, field }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div><p className="text-sm font-medium text-slate-800">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
      <Switch checked={prefs[field]} onCheckedChange={v => setPrefs(p => ({ ...p, [field]: v }))} data-testid={`notif-${field}`} />
    </div>
  );

  const IzniRow = ({ label, desc, field }) => (
    <div className="flex items-center justify-between py-3 border-b border-slate-100 last:border-0">
      <div><p className="text-sm font-medium text-slate-800">{label}</p><p className="text-xs text-slate-400">{desc}</p></div>
      <Switch checked={izni[field]} onCheckedChange={v => setIzni(p => ({ ...p, [field]: v }))} data-testid={`izni-${field}`} />
    </div>
  );

  return (
    <div className="space-y-4" data-testid="notifications-tab">
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base flex items-center gap-2"><Bell className="w-4 h-4" /> Bildirim Tercihleri</CardTitle></CardHeader>
        <CardContent>
          <NotifRow label="E-posta Bildirimleri" desc="Önemli güncellemeler e-posta ile gönderilsin" field="email" />
          <NotifRow label="Push Bildirimleri" desc="Tarayıcı bildirimleri alın" field="push" />
          <NotifRow label="SMS Bildirimleri" desc="Kritik bildirimler SMS ile gönderilsin" field="sms" />
          <NotifRow label="Sesli Arama Bildirimleri" desc="Acil bildirimler sesli arama ile yapılsın" field="voice" />
          <div className="pt-3">
            <Button onClick={handleSave} disabled={saving} size="sm" data-testid="notif-save-btn">
              {saving ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} Tercihleri Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>
      <Card className="shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-base flex items-center gap-2"><ShieldCheck className="w-4 h-4" /> İletişim Kanalları İzni</CardTitle>
          <p className="text-xs text-slate-500 mt-1">KVKK kapsamında hangi kanallar üzerinden sizinle iletişim kurulabileceğini belirleyin.</p>
        </CardHeader>
        <CardContent>
          <IzniRow label="E-posta İletişim İzni" desc="E-posta yoluyla bildirim ve duyuru alınmasını kabul ediyorum" field="email" />
          <IzniRow label="SMS İletişim İzni" desc="SMS yoluyla bildirim ve hatırlatma alınmasını kabul ediyorum" field="sms" />
          <IzniRow label="Sesli Arama İzni" desc="Sesli arama yoluyla acil bildirim alınmasını kabul ediyorum" field="voice" />
          <div className="pt-3">
            <Button onClick={handleSaveIzni} disabled={savingIzni} size="sm" data-testid="izni-save-btn">
              {savingIzni ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Save className="w-4 h-4 mr-1" />} İzinleri Kaydet
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

// Account Tab (Deletion)
const AccountTab = ({ user }) => {
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [requesting, setRequesting] = useState(false);
  const [cancelling, setCancelling] = useState(false);

  const hasPendingDeletion = !!user?.deletion_requested_at;
  const deletionDate = hasPendingDeletion ? new Date(new Date(user.deletion_requested_at).getTime() + 15 * 24 * 60 * 60 * 1000) : null;

  const handleRequestDeletion = async () => {
    setRequesting(true);
    try {
      await api.post('/auth/request-deletion');
      toast.success('Hesap silme talebi olusturuldu');
      setDeleteDialogOpen(false);
      setConfirmText('');
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Talep olusturulamadi'); }
    finally { setRequesting(false); }
  };

  const handleCancelDeletion = async () => {
    setCancelling(true);
    try {
      await api.post('/auth/cancel-deletion');
      toast.success('Hesap silme talebi iptal edildi');
      window.location.reload();
    } catch (e) { toast.error(e.response?.data?.detail || 'Iptal edilemedi'); }
    finally { setCancelling(false); }
  };

  return (
    <div className="space-y-4" data-testid="account-tab">
      {/* Account Info */}
      <Card className="shadow-sm">
        <CardHeader className="pb-2"><CardTitle className="text-base">Hesap Bilgileri</CardTitle></CardHeader>
        <CardContent>
          <div className="space-y-2.5">
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-500">Hesap ID</span><span className="font-mono text-xs text-slate-700">{user?.id?.slice(0, 8)}...</span></div>
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-500">Durum</span><Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-xs">Aktif</Badge></div>
            <div className="flex justify-between py-2 border-b border-slate-100"><span className="text-sm text-slate-500">Rol</span><Badge className="text-xs">{USER_ROLES[user?.role]?.label || user?.role}</Badge></div>
            <div className="flex justify-between py-2"><span className="text-sm text-slate-500">KVKK Onay</span>
              {user?.kvkk_onay ? <Badge className="bg-emerald-50 text-emerald-700 border-emerald-200 border text-xs">Onaylandi</Badge> : <Badge variant="secondary" className="text-xs">Onaylanmadi</Badge>}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Danger Zone */}
      <Card className="shadow-sm border-red-200">
        <CardHeader className="pb-2"><CardTitle className="text-base text-red-700 flex items-center gap-2"><AlertTriangle className="w-4 h-4" /> Tehlikeli Bolge</CardTitle></CardHeader>
        <CardContent>
          {hasPendingDeletion ? (
            <div className="space-y-3">
              <Alert variant="destructive">
                <AlertDescription>
                  Hesabiniz <strong>{deletionDate?.toLocaleDateString('tr-TR')}</strong> tarihinde silinecek. Giris yaparak veya asagidaki butona tiklayarak iptal edebilirsiniz.
                </AlertDescription>
              </Alert>
              <Button variant="outline" onClick={handleCancelDeletion} disabled={cancelling} data-testid="cancel-deletion-btn">
                {cancelling ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <X className="w-4 h-4 mr-1" />} Silme Talebini Iptal Et
              </Button>
            </div>
          ) : (
            <div className="flex items-center justify-between">
              <div><p className="text-sm font-medium text-slate-800">Hesabi Sil</p><p className="text-xs text-slate-400">Hesabiniz 15 gun sonra kalici olarak silinir. Bu sure icinde giris yaparsaniz silme iptal olur.</p></div>
              <Button variant="outline" size="sm" className="text-red-600 border-red-200 hover:bg-red-50" onClick={() => setDeleteDialogOpen(true)} data-testid="request-deletion-btn">
                <Trash2 className="w-3.5 h-3.5 mr-1" /> Hesabi Sil
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-700 flex items-center gap-2"><AlertTriangle className="w-5 h-5" /> Hesabi Sil</DialogTitle>
            <DialogDescription>Bu islem geri alinamaz. Hesabiniz 15 gun sonra kalici olarak silinecektir.</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <p className="text-sm text-slate-600">Onaylamak icin asagiya <strong>"HESABIMI SIL"</strong> yazin:</p>
            <Input value={confirmText} onChange={e => setConfirmText(e.target.value)} placeholder="HESABIMI SIL" data-testid="delete-confirm-input" />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>Vazgec</Button>
            <Button variant="destructive" onClick={handleRequestDeletion} disabled={confirmText !== 'HESABIMI SIL' || requesting} data-testid="delete-confirm-btn">
              {requesting ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Trash2 className="w-4 h-4 mr-1" />} Hesabi Sil
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

// Main Page
const ProfilePage = () => {
  const { user, checkAuth } = useAuth();
  usePageTitle('Profilim');
  const [avatarUrl, setAvatarUrl] = useState(user?.avatar_url);

  useEffect(() => { setAvatarUrl(user?.avatar_url); }, [user?.avatar_url]);

  const userWithAvatar = { ...user, avatar_url: avatarUrl };

  return (
    <div className="max-w-3xl mx-auto space-y-6" data-testid="profile-page">
      <div><h1 className="text-2xl font-bold text-slate-900">Profilim</h1><p className="text-sm text-slate-500">Hesap bilgilerinizi yonetin</p></div>

      {/* Avatar Card */}
      <Card className="shadow-sm">
        <CardContent className="p-6">
          <AvatarSection user={userWithAvatar} onAvatarChange={url => { setAvatarUrl(url); checkAuth(); }} />
        </CardContent>
      </Card>

      {/* Tabs */}
      <Tabs defaultValue="profile" className="w-full">
        <TabsList className="w-full grid grid-cols-4">
          <TabsTrigger value="profile" data-testid="tab-profile"><User className="w-4 h-4 mr-1.5 hidden sm:block" /> Bilgiler</TabsTrigger>
          <TabsTrigger value="security" data-testid="tab-security"><Shield className="w-4 h-4 mr-1.5 hidden sm:block" /> Guvenlik</TabsTrigger>
          <TabsTrigger value="notifications" data-testid="tab-notifications"><Bell className="w-4 h-4 mr-1.5 hidden sm:block" /> Bildirimler</TabsTrigger>
          <TabsTrigger value="account" data-testid="tab-account"><AlertTriangle className="w-4 h-4 mr-1.5 hidden sm:block" /> Hesap</TabsTrigger>
        </TabsList>

        <TabsContent value="profile" className="mt-4">
          <Card className="shadow-sm"><CardContent className="p-5"><ProfileInfoTab user={user} checkAuth={checkAuth} /></CardContent></Card>
        </TabsContent>

        <TabsContent value="security" className="mt-4">
          <SecurityTab user={user} />
        </TabsContent>

        <TabsContent value="notifications" className="mt-4">
          <NotificationsTab user={user} checkAuth={checkAuth} />
        </TabsContent>

        <TabsContent value="account" className="mt-4">
          <AccountTab user={user} />
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default ProfilePage;
